import {Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {FeatureTypeService} from '@catcore/services';
import {Feature} from '@catcore/services/feature/feature';
import {GenericLinkedResource} from '@catcore/types/base';
import {navigateToFeatureResource} from '@catfeatures/shared/utils';
import {FeatureEvidFunctionsService} from '@catmodules/models/feature-evid-functions/services/feature-evid-functions.service';
import {FeatureEvidFunction} from '@catmodules/models/feature-evid-functions/types';
import {DisplayTypes} from '@catui/components/panel/types';
import {LinkItem, StyleType} from '@catui/types';
import {isNullOrUndefined} from 'util';
import {SelectItem} from "primeng/primeng";
import {DropdownComponent} from "@catuiform/components/dropdown/dropdown.component";
import {Subscription} from "rxjs";
import {EntityTypes} from "@catmodels/linked-entities/types";
import {UpdateService} from "@catcore/services/update.service";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {getDirtyFormValues} from "@catcore/utils/form-utils";

const componentBaseName = 'feature-evid-function-form';
const selectorName = 'cat-' + componentBaseName;
const defaultClassNames = [selectorName].join(' ');

/**
 * The FeatureEvidFunctionFormComponent provides the ability to manage the primary information for a
 * FeatureEvidFunction.
 *
 * Additionally to the core-properties of a FeatureEvidFunction the component provides the abilities to add
 * - EvidVids as a FeatureEvidVids
 *
 * to the current active FeatureEvidFunction.
 */
@Component({
  selector: 'cat-feature-evid-function-form',
  templateUrl: './feature-evid-function-form.component.html',
})
export class FeatureEvidFunctionFormComponent implements OnInit, OnDestroy {
  protected _formData: any;

  protected featureSubscription: Subscription;

  @ViewChild('evidDropdown') evidDropdown: DropdownComponent;

  @HostBinding('class') classNames = defaultClassNames;
  @Input() resourceUri: string;
  @Output() onFormSubmit = new EventEmitter();
  @Output() deleteResource = new EventEmitter();

  @Input()
  get formData(): any {
    return this._formData;
  }

  set formData(formData: any) {
    if (!isNullOrUndefined(formData)) {
      this._formData = formData;
      if (this.feature) {
        this.refreshForNewData(formData);
      }
    }
  }

  get modelName() {
    let label = null;
    if (!isNullOrUndefined(this._formData)) {
      label = this._formData._links.model.label;
    }
    return label;
  }

  labels = {
    evidFunction: 'Evid Function',
    featureEvidVids: 'Evid Vids',
    featureEvidService: 'Evid Service',
  };

  LinkedEntityTypes = EntityTypes;
  PanelDisplayTypes = DisplayTypes;

  form: FormGroup;
  linkedPorts: Array<{ href: string }> = [];
  linkedUnits: Array<{ href: string }> = [];
  linkedServices: Array<{ href: string }> = [];
  linkedVids: Array<{ href: string }> = [];
  featureEvidVids: LinkItem[] = [];
  featureEvidServices: SelectItem[];

  styleType = StyleType;

  /**
   * Container for holding the current in use Feature model
   */
  private feature: Feature;

  /**
   * Container holding the selection for the evid service the function is running in
   */
  private evidServiceSelection: any = undefined;
  private evidServiceChanged = false;
  private orgEvidService: any = undefined;

  private currentResourceService;

  constructor(private formBuilder: FormBuilder,
              private featureService: FeatureTypeService,
              private router: Router,
              private featureEvidFunctionService: FeatureEvidFunctionsService,
              private genericService: GenericResourceService,
              private updateService: UpdateService) {
    this.form = formBuilder.group({
      name: formBuilder.control('', []),
      description: formBuilder.control('', []),
      to_be_removed: formBuilder.control(false, [Validators.required])
    });
  }

  ngOnInit() {
    this.featureSubscription = this
      .featureService
      .getCurrent()
      .subscribe((feature: Feature) => {
        if (!isNullOrUndefined(feature)) {
          this.feature = feature;
          if (this._formData) {
            this.refreshForNewData(this._formData);
          }
        }
      });
  }

  ngOnDestroy() {
    if (this.featureSubscription) {
      this.featureSubscription.unsubscribe();
    }
  }

  submitForm() {
    this.onFormSubmit.emit({
        resourceUri: this.resourceUri,
        formData: getDirtyFormValues(this.form),
        success: () => {this.featureService.refreshCurrent()},
      },
    );
  }

  deleteModel($event) {
    this.deleteResource.emit({
      ref: this.resourceUri,
      success: () => this.afterDelete(),
      failure: () => this.afterDeleteFailed(),
    });
  }

  refreshResource() {
    this.currentResourceService.refresh();
  }

  onEntityReceived(entity) {
    this.form.patchValue(entity);
    this.currentResourceService = this.genericService.findServiceForResource(this.resourceUri);
  }

  onUpdate() {
    this.updateService.update(
      () => this.featureEvidFunctionService.updateFromModel(this.formData.id),
      () => this.refreshResource(),
      'feature evid function');
  }

  evidServiceSelectionChanged($event) {
    this.evidServiceSelection = $event.value;
    this.evidServiceChanged = this.evidServiceSelection !== this.orgEvidService;
    $event.originalEvent.stopPropagation();
  }

  updateEvidService() {
    this.onFormSubmit.emit({
        resourceUri: this.resourceUri,
        formData: {service: this.evidServiceSelection},
        success: () => this.updateEvidServiceSuccess(),
        failure: () => this.refreshForNewData(this._formData),
      },
    );
  }

  updateEvidServiceSuccess() {
    this.orgEvidService = undefined;
    this.evidServiceSelection = undefined;
    this.evidServiceChanged = false;
    this.refreshForNewData(this._formData);
  }

  private afterDelete() {
    this.featureService.refreshCurrent();
    this.router.navigate(['features', this.feature.id, 'evid-functions']);
  }
  private afterDeleteFailed() {}

  private refreshForNewData(featureEvidFunction: FeatureEvidFunction) {
    this.refreshLinkedPorts(featureEvidFunction);
    this.refreshLinkedUnits(featureEvidFunction);
    this.refreshLinkedVids(featureEvidFunction);
    this.refreshLinkedServices(featureEvidFunction);
    this.refreshVids(featureEvidFunction);
    this.refreshEvidServices(featureEvidFunction);
    this.form.patchValue(featureEvidFunction);
  }

  private refreshLinkedPorts(featureEvidFunction: FeatureEvidFunction) {
    this.linkedPorts = this.featureEvidFunctionService.extractLinkedPortsFor(featureEvidFunction);
  }

  private refreshLinkedUnits(featureEvidFunction: FeatureEvidFunction) {
    this.linkedUnits = this.featureEvidFunctionService.extractLinkedUnitsFor(featureEvidFunction);
  }

  private refreshLinkedVids(featureEvidFunction: FeatureEvidFunction) {
    this.linkedVids = this.featureEvidFunctionService.extractLinkedVidsFor(featureEvidFunction);
  }

  private refreshLinkedServices(featureEvidFunction: FeatureEvidFunction) {
    this.linkedServices = this.featureEvidFunctionService.extractLinkedServicesFor(featureEvidFunction);
  }

  private refreshVids(featureEvidFunction: FeatureEvidFunction) {
    const extractedInfos = this.featureEvidFunctionService.extractEvidVidsFor(featureEvidFunction);
    this.featureEvidVids = this.buildLinkedList(extractedInfos);
  }

  private refreshEvidServices(featureEvidFunction: FeatureEvidFunction) {
    const services = this.feature._links['action:services'];
    const items: SelectItem[] = [];

    items.push({
      label: 'Auto-resolved by CAT',
      value: null,
    });
    services.forEach((service) => {
      if (service.label.startsWith('EVID')) {
        items.push({
          label: service.label,
          value: service.href,
        });
      }
    });

    this.featureEvidServices = items;
    // set initial value if given
    if (!isNullOrUndefined(featureEvidFunction._links.service)) {
      this.orgEvidService = featureEvidFunction._links.service.href;
      this.evidDropdown.writeValue(featureEvidFunction._links.service.href);
    }
  }

  private buildLinkedList(modelListData: GenericLinkedResource[]): LinkItem[] {
    const linkedItems: LinkItem[] = [];

    modelListData.forEach((item: GenericLinkedResource) => {
      linkedItems.push({
        label: item.label,
        navigate: (router: Router) => navigateToFeatureResource(router, this.feature, item),
      });
    });
    return linkedItems;
  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/feature-evid-functions/components/feature-evid-function-form/feature-evid-function-form.component.ts